import hashSum from 'hash-sum'
// Simple query that determines if an object is actually an object and not an array or null
export const isObject = (obj) => obj !== null && typeof obj === 'object' && !(obj instanceof Array)

// Will search an object to see if it contains a specified property and that said property is not null
export const contains = (obj, prop, depth = -1, allowNull = false) =>
  Object.keys({ ...obj }).some(
    (k) =>
      (k === prop && (allowNull || obj[k] !== null)) ||
      (isObject(obj[k]) && depth !== 0 && contains(obj[k], prop, depth - 1)),
  )

// Creates a universal id for an element.
// This is not a replacement function for backend id's but only for the cases where an id is needed for frontend stuff
// is as a key for a new element in a vue loop or similar. Use normal backend id's if available
export const uuid = () => {
  if (typeof window?.crypto?.randomUUID === 'function') return window.crypto.randomUUID()
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    // eslint-disable-next-line no-bitwise
    (c ^ (crypto.getRandomValues(new Uint16Array(1))[0] & (15 >> (c / 4)))).toString(16),
  )
}

/**
 * Checks if a given string is a valid UUID (versions 1-5).
 *
 * @param {string} id - The string to check.
 * @returns {boolean} - Returns `true` if the string is a valid UUID, otherwise `false`.
 */
export const isUUID = (id) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)

// Helps find a specific property in an object. Searches its children and will greedily return the first found property
// that fits the given key
export const findProperty = (key, obj) => {
  if (typeof obj !== 'object' || obj === null) return undefined
  if (obj.hasOwnProperty(key)) {
    return obj[key]
  }

  for (const k in obj) {
    if (typeof obj[k] === 'object' && obj[k] !== null) {
      const result = findProperty(key, obj[k])
      if (result !== undefined) {
        return result
      }
    }
  }

  return undefined
}

// Magic inline function that takes an object and removes any null properties from it and then returns it
// the {... obj} converts a null value to an empty object so we don't crash in that case
export const clean = (obj) => {
  if (obj === null) {
    return null
  }
  const objCleaned = {}
  Object.entries(obj).forEach(([k, v]) => {
    if (isObject(v)) {
      // We clean any child objects that we might have
      objCleaned[k] = clean(v)
    } else if (v === null) {
      // Skip to remove from cleaned object
    } else {
      objCleaned[k] = v
    }
  })
  return objCleaned
}

// Merges two objects, with the second object taking priority when conflicts happen. It will also try and merge arrays,
// but duplication can occur here.
export const merge = (o1, o2) => {
  if (o1 === null || o1 === undefined) {
    return o2
  }
  if (o2 === null || o2 === undefined) {
    return o1
  }
  if ((o1 instanceof Array && o2 instanceof Array) || (isObject(o1) && o2 instanceof Array)) {
    const isApolloNormalizedArray = o1
      .map(Object.keys)
      .concat(o2.map(Object.keys))
      .flat(1)
      .every((key) => key === '__ref')

    if (isApolloNormalizedArray) {
      const existingKeys = new Set(o1.map(Object.values).flat(1))
      const o3 = o2.filter(({ __ref }) => !existingKeys.has(__ref))

      return [...o1, ...o3]
    }

    return [...o1, ...o2]
  }

  const objMerged = { ...o1, ...o2 }
  const keys = [...new Set([...Object.keys(o1), ...Object.keys(o2)])]

  keys
    .filter(
      (k) => o1[k] instanceof Array || isObject(o1[k]) || o2[k] instanceof Array || isObject(o2[k]),
    )
    .forEach((k) => {
      objMerged[k] = merge(o1[k], o2[k])
    })

  return objMerged
}

// Does the same as clean by removing null, but also removes values such as undefined, 0, '', [] and {}
export const cleanStrict = (obj) => {
  if (obj === null) {
    return null
  }
  const objCleaned = {}
  Object.entries(obj).forEach(([k, v]) => {
    if (isObject(v)) {
      if (Object.keys(v).length > 0) {
        // We clean any child objects that we might have
        objCleaned[k] = cleanStrict(v)
      }
    } else if (
      v === null ||
      v === '' ||
      v === 0 ||
      v === undefined ||
      (v instanceof Array && v.length === 0)
    ) {
      // Skip to remove from cleaned object
    } else {
      objCleaned[k] = v
    }
  })
  return objCleaned
}

export const filterObject = (obj, conditional) => {
  const objFiltered = {}
  Object.entries(obj).forEach(([k, v]) => {
    if (conditional(k, v)) {
      objFiltered[k] = v
    }
  })
  return objFiltered
}

/**
 * Deep clones an object and all sub arrays that it might contain.
 * Helps in places where modifications need to be done to objects, but they are locked as read only or are reactive
 * @param {Object} obj
 * @returns {{}|*}
 */
export const deepClone = (obj) => {
  if (!isObject(obj) && !(obj instanceof Array)) return obj
  const clone = {}
  Object.keys({ ...obj }).forEach((k) => {
    const val = obj[k]
    if (isObject(val)) {
      clone[k] = deepClone(val)
    } else if (val instanceof Array) {
      clone[k] = val.map(deepClone)
    } else {
      clone[k] = val
    }
  })
  return obj instanceof Array ? Object.values(clone) : clone
}

// Magic function that will take an object and flatten it ie {hello: {world: ":)"}} will become {world: ":)"}
// It ignores arrays, so if {hello: [world1, world2]} it will stay as {hello: [world1, world2]}
export const flat = (o) => {
  let obj = deepClone(o)

  Object.keys(obj).forEach((k) => {
    let key = k

    // Here we ensure that we are only working on objects and not null or arrays
    if (isObject(obj[k])) {
      // We check if we have decedents with the same name, and augment our key if that is the case
      if (contains(obj[k], k)) {
        const nk = k + hashSum(obj)
        obj[nk] = obj[k]
        key = nk
      }

      // We flatten the child object, then merge the resulting values with ourselves and then delete the old child
      obj = { ...obj, ...flat(obj[key]) }
      delete obj[key]
    }
  })

  // And we clean any mess we might have made along the way
  return clean(obj)
}

// Makes an object log-able by ie sentry
export const logAble = (o, split = ',', max = 0) => {
  if (!isObject(o) && !(o instanceof Array)) {
    return o
  }
  const obj = { ...o }
  Object.keys(obj).forEach((key) => {
    if (obj[key] instanceof Array) {
      obj[key] = obj[key]
        .map((v) => logAble(v, split, max))
        .splice(-max)
        .join(split)
    } else {
      obj[key] = logAble(obj[key], split, max)
    }
  })
  return obj
}

// Checks whether object is iterable
export const isIterable = (o) => {
  if (o === null || o === undefined) {
    return false
  }
  return typeof o[Symbol.iterator] === 'function'
}

/**
 * This function allows for accessing properties using a dot formatted string
 * ie 'path.to.prop' or 'path.to.0.array.prop'
 * It returns if found the resulting data in the specified prop
 *
 * @param {Object} obj object to access prop in
 * @param {String} key the dot formatted string path to prop in object
 * @returns {*}
 */
export const dotAccess = (obj, key = '') => {
  let data = obj
  key.split('.').forEach((k) => {
    if (k !== '') {
      data = data?.[k]
    }
  })
  return data
}

// Function that does the actual work for deepObjectMap
const _deepObjectMap = (obj, keys, i, fn) => {
  if (i === keys.length) {
    // We have reached a leaf node and apply our function
    if (obj instanceof Array) return obj.map(fn)
    return fn(obj)
  }

  // Navigate the object tree recursively and save our mapped results on the object
  const key = keys[i]
  // eslint-disable-next-line @typescript-eslint/no-shadow
  if (obj instanceof Array) obj.map((obj) => _deepObjectMap(obj[key], keys, i + 1, fn))
  if (isObject(obj) && key in obj) {
    // eslint-disable-next-line no-param-reassign
    obj[key] = _deepObjectMap(obj[key], keys, i + 1, fn)
  }

  return obj
}

/**
 * Map a specific value of an object or array of objects. Useful for very deep object with arrays of objects where small
 * updates to props needs to be done.
 * @param {Array|Object} obj
 * @param {string} key dot seperated key similar to dotAccess ie 'path.to.my.prop' no need for special tokens for arrays
 * @param {function} fn function that takes value from the final .key as argument and returns a new mapped value
 * @returns {Array|Object} It returns a copy of the original object with the mapping modifications applied
 */
export const deepObjectMap = (obj, key, fn) => _deepObjectMap(deepClone(obj), key.split('.'), 0, fn)

/**
 * Convert a value to a specific primitive type
 * @param {*} val
 * @param {boolean|symbol|string|bigint|number|undefined|null|Number|BigInt|Symbol|String|Boolean} type
 * @returns {boolean|symbol|string|bigint|number|undefined|null}
 */
export const convertToPrimitive = (val, type) => {
  if (type === Number || typeof type === 'number') return Number(val)
  if (type === BigInt || typeof type === 'bigint') return BigInt(val)
  if (type === Symbol || typeof type === 'symbol') return Symbol(val)
  if (type === undefined || typeof type === 'undefined') return undefined
  if (type === String || typeof type === 'string') return String(val)
  if (type === Boolean || typeof type === 'boolean')
    return Boolean(val === 'false' || val === '0' ? false : val)
  return null // The last primitive is null which gets typeof === 'object'
}

// Removes empty strings from arrays
export const removeEmpty = (arr) => arr.filter((el) => el !== '')

// Takes a string and converts it into an equivalent graphql enum
export const stringToEnum = (str) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`).toUpperCase()

// Works as a debounce function similar to lodashes implementation
export const debounce = (func, wait, immediate) => {
  let timeout
  return (...args) => {
    // eslint-disable-next-line prefer-const
    const context = this
    // eslint-disable-next-line prefer-rest-params,no-undef
    const later = () => {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export const objectContainsTruthyEntries = (obj) =>
  Object.keys({ ...obj })
    // eslint-disable-next-line prefer-arrow-callback
    .some(function checkForTruthyValues(key) {
      return !!obj[key]
    })

export const textHelpers = {
  truncate: (text, maxLength = 40) =>
    text?.length > maxLength ? `${text.substring(0, maxLength)}...` : text,

  keyToHumanReadable: (keyName) => {
    if (typeof keyName !== 'string') {
      return null
    }
    const keyMatch = keyName.match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g,
    )
    if (keyMatch === null) {
      return null
    }
    return keyMatch.map((x) => x.toLowerCase()).join(' ')
  },

  nameStringToInitials: (name, numInitials = 3) => {
    if (typeof name !== 'string') {
      return ''
    }

    return name
      .split(' ')
      .splice(0, numInitials)
      .reduce((acc, curr) => {
        if (curr.length > 0) {
          return acc + curr[0]
        }
        return acc
      }, '')
  },

  capitalizeFirstLetter: (str) => str.charAt(0).toUpperCase() + str.slice(1),

  getOrdinal: (number) => {
    if (typeof number !== 'number' || isNaN(number)) {
      throw new Error('Input must be a valid number.')
    }

    const suffixes = ['th', 'st', 'nd', 'rd']
    const lastDigit = number % 10
    const secondLastDigit = Math.floor((number % 100) / 10)

    let suffixIndex = 0

    if (secondLastDigit === 1 || lastDigit > 3) {
      suffixIndex = 0
    } else if (lastDigit === 1) {
      suffixIndex = 1
    } else if (lastDigit === 2) {
      suffixIndex = 2
    } else if (lastDigit === 3) {
      suffixIndex = 3
    }

    return suffixes[suffixIndex]
  },
}

export const setdefault = (obj, key, defaultValue) => {
  if (!obj.hasOwnProperty(key)) {
    // eslint-disable-next-line no-param-reassign
    obj[key] = defaultValue
  }
  return obj[key]
}

export const isEmpty = (value) => {
  if (value && typeof value === 'object' && !(value instanceof Array)) {
    for (const item of Object.values(value)) {
      if (!isEmpty(item)) {
        return false
      }
    }

    return true
  }

  if (value && value instanceof Array) {
    return value.length === 0
  }

  return value === undefined || value === null || value === ''
}

export const convertEnumToDisplayText = (enumValue) =>
  // Replace underscores with spaces and capitalize the first letter of each word
  enumValue
    ? enumValue
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : enumValue

// each argument is an array or list. It will then yield a generator allowing one to sequentially go over elements together
export function* zip(...args) {
  for (let i = 0; i < args[0]?.length; i++) {
    const emit = []
    for (const arr of args) {
      emit.push(arr[i])
    }
    yield emit
  }
}

export function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}
